import { Success, Failure, collect } from 'folktale/validation'
import { fromNullable } from 'folktale/maybe'
import { lensPath, lensProp, any, all, Lens, pipe, view, map } from 'ramda'
import BookingUpsertModel from '@v/bookings/edit/models/BookingUpsertModel.js'

const buildValidation = (...args) => model => collect(args.map(v => v(model)))

/** @type { (prefix: object) => Lens } */
const lensToSegment = ({ classificationsProp, segmentsProp }) =>
  lensPath([classificationsProp, 0, segmentsProp])

const entityHasField = ({ prop, classificationsProp, segmentsProp }) =>
  pipe(
    view(lensToSegment({ classificationsProp, segmentsProp })),
    fromNullable,
    map(any(s => s[prop] > 0)),
    maybe => maybe.getOrElse(false),
  )

// === Number of persons validation ===

const bookingHasSegmentedVisitors = entityHasField({
  prop: 'NumberOfPersons',
  classificationsProp: 'NumberOfPersonsPerClassification',
  segmentsProp: 'NumberOfPersonsPerSegment',
})

const bookingHasVisitors = b => b.NumberOfPersons || bookingHasSegmentedVisitors(b)

const hasNumberOfPersons = m =>
  bookingHasVisitors(m.booking) ? Success(m) : Failure(['Booking does not have any visitors'])

const isUpdateOrHasCustomer = m => (m.booking.Key ? shouldNotHaveCustomer(m) : hasValidCustomer(m))

// === BookingProducts validation ===

/** @type {(m: BookingUpsertModel) => Validation} */
export const hasBookingProductsWithQuantities = m =>
  m.bookingProducts?.length
    ? bookingProductsHaveQuantity(m)
    : Failure(['Booking does not have any products'])

const bookingProductsHaveQuantity = m =>
  allBookingProductHasQuantity(m)
    ? Success(m)
    : Failure(['All booking products must have quantity'])

const allBookingProductHasQuantity = m => all(bookingProductHasQuantity)(m.bookingProducts)

const bookingProductHasSegmentQuantity = entityHasField({
  prop: 'Quantity',
  classificationsProp: 'QuantitiesPerClassification',
  segmentsProp: 'QuantitiesPerSegment',
})

const bookingProductHasQuantity = bookingProduct =>
  pipe(view(lensProp('QuantitiesPerClassification')), x =>
    x && x.length > 0
      ? bookingProductHasSegmentQuantity(bookingProduct)
      : bookingProduct.ProductQuantity > 0,
  )(bookingProduct)

// === Customer validation ===

const hasValidCustomer = m =>
  m.booking.CustomerKey ? shouldNotHaveCustomer(m) : shouldHaveCustomer(m)

function shouldHaveCustomer(m) {
  return validateCustomer(m.customer).fold(
    x => Failure(x),
    () => Success(m),
  )
}

const shouldNotHaveCustomer = m =>
  m.customer
    ? Failure(['Booking should not have a customer if it already has a customerKey'])
    : Success(m)

const hasFirstname = c => (c.Firstname ? Success(c) : Failure(['Customer must have a first name']))

const hasLastname = c => (c.Lastname ? Success(c) : Failure(['Customer must have a last name']))

// todo: validate email & company email: if it's defined it should contain an "@" and a "."

const validateExistingCustomer = buildValidation(hasFirstname, hasLastname)

export const validateCustomer = c =>
  c ? validateExistingCustomer(c) : Failure(['Booking does not have a customer'])

export default buildValidation(
  hasNumberOfPersons,
  hasBookingProductsWithQuantities,
  isUpdateOrHasCustomer,
)
